import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  PlusCircle, FileText, MessageSquare, ArrowRight,
  MapPin, Wrench, Clock, Heart, Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useMyAnnonces, getUrgencyLabel } from "@/hooks/useAnnonces";
import { useUnreadCount } from "@/hooks/useUnreadCount";
import { useSearchArtisans } from "@/hooks/useSearchArtisans";
import { useFavoriteArtisanIds, useToggleFavoriteArtisan } from "@/hooks/useFavoriteArtisans";
import ArtisanBadges from "@/components/ArtisanBadges";
import VerificationBadge from "@/components/VerificationBadge";
import { cn } from "@/lib/utils";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { useLanguage } from "@/hooks/useLanguage";

const ClientHomeContent = () => {
  const lp = useLocalizedPath();
  const { t, locale } = useLanguage("client");

  const { data: myAnnonces = [] } = useMyAnnonces();
  const unreadCount = useUnreadCount();

  // Filtre spécialité artisan
  const [artisanSpecialite, setArtisanSpecialite] = useState("tous");

  const { data: artisans = [], isLoading: artisansLoading } = useSearchArtisans({
    specialite: artisanSpecialite !== "tous" ? artisanSpecialite : undefined,
  });
  const { data: favoriteIds } = useFavoriteArtisanIds();
  const toggleFavorite = useToggleFavoriteArtisan();

  const activeAnnonces = myAnnonces.filter((a) => a.status === "published").slice(0, 3);
  const displayedArtisans = artisans.slice(0, 6);

  const SPECIALITE_FILTERS = [
    { value: "tous",        label: t("home.filter_all") },
    { value: "Plomberie",   label: t("home.filter_plomberie") },
    { value: "Électricité", label: t("home.filter_electricite") },
    { value: "Serrurerie",  label: t("home.filter_serrurerie") },
  ];

  return (
    <div className="container py-6 space-y-8 pb-24">

      {/* CTA Poster */}
      <Link to={lp("/poster-annonce")}>
        <Button variant="hero" size="xl" className="w-full gap-2">
          <PlusCircle className="h-5 w-5" />
          {t("home.cta_poster")}
        </Button>
      </Link>

      {/* Mes annonces en cours */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {t("home.my_annonces")}
          </h2>
          <Link to={lp("/mes-annonces")}>
            <Button variant="ghost" size="sm" className="text-xs">{t("home.see_all")}</Button>
          </Link>
        </div>

        {activeAnnonces.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center">
              <FileText className="mx-auto h-8 w-8 text-muted-foreground/30" />
              <p className="mt-2 text-sm text-muted-foreground">{t("home.no_annonces")}</p>
              <Link to={lp("/poster-annonce")}>
                <Button variant="outline" size="sm" className="mt-3">
                  {t("home.create_first")}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {activeAnnonces.map((annonce) => (
              <Link key={annonce.id} to={lp(`/annonce/${annonce.id}`)}>
                <Card className="hover:border-primary/20 transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1 min-w-0 flex-1">
                        <h3 className="font-display text-sm font-semibold truncate">{annonce.title}</h3>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />{annonce.commune}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />{getUrgencyLabel(annonce.urgency, locale)}
                          </span>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Nouveaux messages */}
      <section className="space-y-3">
        <Link to={lp("/messagerie")}>
          <Card className="hover:border-primary/20 transition-all">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold">{t("home.messages")}</h3>
                  <p className="text-xs text-muted-foreground">
                    {unreadCount > 0
                      ? t("home.unread_messages", { count: unreadCount })
                      : t("home.no_messages")}
                  </p>
                </div>
              </div>
              {unreadCount > 0 && (
                <Badge className="bg-destructive text-destructive-foreground">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </Badge>
              )}
            </CardContent>
          </Card>
        </Link>
      </section>

      {/* ── Trouver un artisan ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            {t("home.find_artisan")}
          </h2>
          <Link to={lp("/recherche-artisans")}>
            <Button variant="ghost" size="sm" className="text-xs gap-1">
              {t("home.see_all")} <ArrowRight className="h-3 w-3" />
            </Button>
          </Link>
        </div>

        {/* Filtres spécialité — chips horizontaux */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none -mx-1 px-1">
          {SPECIALITE_FILTERS.map((spec) => (
            <button
              key={spec.value}
              onClick={() => setArtisanSpecialite(spec.value)}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap",
                artisanSpecialite === spec.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {spec.label}
            </button>
          ))}
        </div>

        {/* Liste artisans */}
        {artisansLoading ? (
          <p className="text-center text-sm text-muted-foreground py-6">{t("home.loading")}</p>
        ) : displayedArtisans.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center">
              <Users className="mx-auto h-8 w-8 text-muted-foreground/30" />
              <p className="mt-2 text-sm text-muted-foreground">
                {t("home.no_artisans")}
                {artisanSpecialite !== "tous" ? ` en ${artisanSpecialite}` : ""}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {displayedArtisans.map((artisan) => {
              const isFav = favoriteIds?.has(artisan.user_id);
              return (
                <Card key={artisan.id} className="hover:border-primary/20 transition-all">
                  <CardContent className="flex gap-3 p-3">
                    {/* Photo / icône */}
                    <Link
                      to={lp(`/artisan/${artisan.user_id}`)}
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary overflow-hidden"
                    >
                      {artisan.logo_url ? (
                        <img
                          src={artisan.logo_url}
                          alt={artisan.display_name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Wrench className="h-6 w-6 text-muted-foreground" />
                      )}
                    </Link>

                    {/* Infos */}
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-start justify-between gap-1">
                        <Link to={lp(`/artisan/${artisan.user_id}`)} className="min-w-0 flex items-center gap-1.5">
                          <p className="font-display text-sm font-semibold truncate hover:text-primary transition-colors">
                            {artisan.display_name}
                          </p>
                          <VerificationBadge
                            type={artisan.type}
                            verificationStatus={artisan.verification_status ?? undefined}
                            className="text-[10px] shrink-0"
                          />
                        </Link>
                        {/* Bouton favori */}
                        <button
                          onClick={() => toggleFavorite.mutate(artisan.user_id)}
                          className="shrink-0 p-1.5 -m-1 rounded-lg hover:bg-muted transition-colors"
                          aria-label={isFav ? t("home.remove_fav") : t("home.add_fav")}
                        >
                          <Heart
                            className={cn(
                              "h-4 w-4 transition-colors",
                              isFav ? "fill-destructive text-destructive" : "text-muted-foreground"
                            )}
                          />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">{artisan.specialite}</p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">{artisan.commune}, {artisan.wilaya}</span>
                      </div>
                      <ArtisanBadges
                        isReactiveBadge={artisan.is_reactive_badge ?? undefined}
                        profileCompletionScore={artisan.profile_completion_score ?? undefined}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Voir plus si plus de 6 résultats */}
        {artisans.length > 6 && (
          <Link to={lp("/recherche-artisans")}>
            <Button variant="outline" className="w-full" size="sm">
              {t("home.see_all_artisans")} <ArrowRight className="ms-1.5 h-3.5 w-3.5" />
            </Button>
          </Link>
        )}
      </section>
    </div>
  );
};

export default ClientHomeContent;
